import { useState, useRef, useCallback, useEffect } from "react";
import { trpc } from "../lib/trpc.js";

// ─── Types ───────────────────────────────────────────────────────────────────

export type VoiceState =
  | "idle"           // Nenhuma atividade de voz
  | "wake-listening" // Escutando wake word "Olá Megan"
  | "listening"      // Gravando input do usuário
  | "processing"     // Transcrevendo com Whisper
  | "speaking";      // Megan falando (TTS)

interface UseVoiceOptions {
  onTranscription?: (text: string) => void;
  onError?: (error: string) => void;
  silenceTimeout?: number; // ms sem falar para auto-stop (default: 6000)
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useVoice(options: UseVoiceOptions = {}) {
  const { onTranscription, onError, silenceTimeout = 6000 } = options;

  const [state, setState] = useState<VoiceState>("idle");
  const [volume, setVolume] = useState(0);
  const [wakeWordActive, setWakeWordActive] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const volumeFrameRef = useRef<number>(0);
  const wakeRecogRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const transcribeMutation = trpc.chat.transcribeAudio.useMutation();
  const ttsMutation = trpc.chat.textToSpeech.useMutation();

  // ─── Cleanup ─────────────────────────────────────────────────────────────

  const cleanup = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (volumeFrameRef.current) {
      cancelAnimationFrame(volumeFrameRef.current);
      volumeFrameRef.current = 0;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    audioChunksRef.current = [];
    setVolume(0);
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
      stopWakeWordListener();
    };
  }, [cleanup]);

  // ─── Volume Monitoring ───────────────────────────────────────────────────

  const monitorVolume = useCallback(() => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;
    const data = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (!analyserRef.current) return;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      const normalized = Math.min(1, rms * 4); // Amplify for visual
      setVolume(normalized);

      // Silence detection: reset timer if volume > threshold
      if (normalized > 0.05) {
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
        }
        silenceTimerRef.current = setTimeout(() => {
          // Auto-stop after silence
          stopRecording();
        }, silenceTimeout);
      }

      volumeFrameRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, [silenceTimeout]);

  // ─── Start Recording ─────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    try {
      cleanup();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });
      streamRef.current = stream;

      // Setup audio analysis for volume
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Setup MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const chunks = audioChunksRef.current;
        if (chunks.length === 0) {
          setState("idle");
          return;
        }
        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size < 1000) {
          // Too small, probably just noise
          setState("idle");
          onError?.("Áudio muito curto. Tente novamente.");
          return;
        }
        await processAudio(blob);
      };

      recorder.start(250); // Collect data every 250ms
      setState("listening");

      // Start volume monitoring
      monitorVolume();

      // Start silence timer
      silenceTimerRef.current = setTimeout(() => {
        stopRecording();
      }, silenceTimeout);
    } catch (err: any) {
      console.error("[Voice] Error starting recording:", err);
      onError?.(err.message || "Erro ao acessar microfone");
      setState("idle");
    }
  }, [cleanup, monitorVolume, silenceTimeout, onError]);

  // ─── Stop Recording ──────────────────────────────────────────────────────

  const stopRecording = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (volumeFrameRef.current) {
      cancelAnimationFrame(volumeFrameRef.current);
      volumeFrameRef.current = 0;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      setState("processing");
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setVolume(0);
  }, []);

  // ─── Process Audio (upload + transcribe) ─────────────────────────────────

  const processAudio = useCallback(async (blob: Blob) => {
    setState("processing");
    try {
      // Convert blob to base64 data URL
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const dataUrl = await base64Promise;

      // For now, we'll send the base64 directly as a data URL
      // The backend will handle downloading it
      // We need to upload to R2 first, then send the URL
      const result = await transcribeMutation.mutateAsync({
        audioUrl: dataUrl,
      });

      if (result.text && result.text.trim()) {
        onTranscription?.(result.text.trim());
      } else {
        onError?.("Não consegui entender o áudio. Tente novamente.");
      }
    } catch (err: any) {
      console.error("[Voice] Transcription error:", err);
      onError?.(err.message || "Erro na transcrição");
    } finally {
      setState("idle");
    }
  }, [transcribeMutation, onTranscription, onError]);

  // ─── TTS: Megan speaks ──────────────────────────────────────────────────

  const speak = useCallback(async (text: string, voice?: string) => {
    try {
      setState("speaking");
      const result = await ttsMutation.mutateAsync({
        text,
        voice: (voice as any) || "nova",
      });

      // Play audio
      const audio = new Audio(`data:${result.mimeType};base64,${result.audioBase64}`);
      audioRef.current = audio;
      audio.onended = () => {
        setState("idle");
        audioRef.current = null;
      };
      audio.onerror = () => {
        setState("idle");
        audioRef.current = null;
      };
      await audio.play();
    } catch (err: any) {
      console.error("[Voice] TTS error:", err);
      setState("idle");
    }
  }, [ttsMutation]);

  // ─── Stop Speaking ───────────────────────────────────────────────────────

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setState("idle");
  }, []);

  // ─── Wake Word: "Olá Megan" ─────────────────────────────────────────────

  const startWakeWordListener = useCallback(() => {
    // Use Web Speech API for wake word detection (lightweight, runs in browser)
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      onError?.("Reconhecimento de voz não suportado neste navegador");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "pt-BR";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.toLowerCase().trim();
        if (
          transcript.includes("olá megan") ||
          transcript.includes("ola megan") ||
          transcript.includes("hey megan") ||
          transcript.includes("oi megan")
        ) {
          // Wake word detected! Start recording
          recognition.stop();
          setWakeWordActive(false);
          setState("wake-listening");
          // Small delay then start recording
          setTimeout(() => {
            startRecording();
          }, 300);
          return;
        }
      }
    };

    recognition.onerror = (event: any) => {
      if (event.error !== "no-speech" && event.error !== "aborted") {
        console.error("[Voice] Wake word error:", event.error);
      }
      // Restart on non-fatal errors
      if (event.error === "no-speech" || event.error === "network") {
        try { recognition.start(); } catch {}
      }
    };

    recognition.onend = () => {
      // Auto-restart if still in wake-listening mode
      if (wakeWordActive) {
        try { recognition.start(); } catch {}
      }
    };

    try {
      recognition.start();
      wakeRecogRef.current = recognition;
      setWakeWordActive(true);
      setState("wake-listening");
    } catch (err: any) {
      console.error("[Voice] Wake word start error:", err);
      onError?.("Erro ao iniciar escuta de wake word");
    }
  }, [onError, startRecording, wakeWordActive]);

  const stopWakeWordListener = useCallback(() => {
    if (wakeRecogRef.current) {
      try { wakeRecogRef.current.stop(); } catch {}
      wakeRecogRef.current = null;
    }
    setWakeWordActive(false);
    if (state === "wake-listening") {
      setState("idle");
    }
  }, [state]);

  // ─── Toggle (main action button) ────────────────────────────────────────

  const toggle = useCallback(() => {
    switch (state) {
      case "idle":
        startRecording();
        break;
      case "listening":
        stopRecording();
        break;
      case "speaking":
        stopSpeaking();
        break;
      case "wake-listening":
        stopWakeWordListener();
        break;
      case "processing":
        // Can't cancel processing
        break;
    }
  }, [state, startRecording, stopRecording, stopSpeaking, stopWakeWordListener]);

  return {
    state,
    volume,
    wakeWordActive,
    isRecording: state === "listening",
    isProcessing: state === "processing",
    isSpeaking: state === "speaking",
    isWakeListening: state === "wake-listening",
    toggle,
    startRecording,
    stopRecording,
    speak,
    stopSpeaking,
    startWakeWordListener,
    stopWakeWordListener,
  };
}
